import React, { useState, useCallback } from 'react';
import {
  Bluetooth,
  Wifi,
  Printer,
  ScanBarcode,
  Monitor,
  Search,
  Plus,
  Trash2,
  RefreshCw,
  Power,
  Unplug,
  Plug,
  Loader,
  X,
  CircleCheck,
  CircleAlert,
  Inbox,
  Globe,
  Signal,
} from 'lucide-react';
import { useDeviceManager } from '../hooks/useDeviceManager';
import type { ManagedDevice } from '../hooks/useDeviceManager';

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getDeviceIcon(type: string) {
  switch (type) {
    case 'printer': return Printer;
    case 'scanner': return ScanBarcode;
    default: return Monitor;
  }
}

function getConnectionIcon(metadata: ManagedDevice['metadata']) {
  if (!metadata) return Globe;
  return metadata.connectionType === 'bluetooth' ? Bluetooth : Wifi;
}

/* ────────── Add WiFi Device Modal ────────── */

interface AddWifiModalProps {
  onAdd: (name: string, type: 'printer' | 'scanner' | 'other', ip: string, port: number) => void;
  onClose: () => void;
  saving: boolean;
}

const AddWifiDeviceModal: React.FC<AddWifiModalProps> = ({ onAdd, onClose, saving }) => {
  const [name, setName] = useState('');
  const [type, setType] = useState<'printer' | 'scanner' | 'other'>('printer');
  const [ip, setIp] = useState('');
  const [port, setPort] = useState('9100');

  const canSave = name.trim() && ip.trim() && port.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Wifi className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Add WiFi Device</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Enter device network details</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Device Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Warehouse Zebra Printer"
              className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 placeholder:text-gray-400"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Device Type</label>
            <div className="flex gap-2">
              {(['printer', 'scanner', 'other'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setType(t)}
                  className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                    type === t
                      ? 'bg-blue-500 text-white shadow-sm'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">IP Address</label>
              <input
                type="text"
                value={ip}
                onChange={(e) => setIp(e.target.value)}
                placeholder="192.168.1.100"
                className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 placeholder:text-gray-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Port</label>
              <input
                type="text"
                value={port}
                onChange={(e) => setPort(e.target.value)}
                placeholder="9100"
                className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 placeholder:text-gray-400"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => canSave && onAdd(name.trim(), type, ip.trim(), parseInt(port, 10) || 9100)}
            disabled={!canSave || saving}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add Device
          </button>
        </div>
      </div>
    </div>
  );
};

/* ────────── Bluetooth Scan Type Picker ────────── */

interface BtTypeSelectorProps {
  onSelect: (type: 'printer' | 'scanner' | 'other') => void;
  onClose: () => void;
}

const BtTypeSelector: React.FC<BtTypeSelectorProps> = ({ onSelect, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
      <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Bluetooth className="h-5 w-5 text-white" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Device Type</h2>
        </div>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
          <X className="h-5 w-5 text-gray-500" />
        </button>
      </div>
      <div className="p-5 space-y-2">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">What kind of device are you connecting?</p>
        {([
          { type: 'printer' as const, label: 'Printer', icon: Printer, desc: 'Thermal, label, or receipt printer' },
          { type: 'scanner' as const, label: 'Scanner', icon: ScanBarcode, desc: 'Barcode or QR code scanner' },
          { type: 'other' as const, label: 'Other', icon: Monitor, desc: 'NFC reader, mobile computer, etc.' },
        ]).map(({ type, label, icon: Icon, desc }) => (
          <button
            key={type}
            onClick={() => onSelect(type)}
            className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700 transition-colors text-left"
          >
            <Icon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{desc}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  </div>
);

/* ────────── Device Card ────────── */

interface DeviceCardProps {
  device: ManagedDevice;
  liveConnected: boolean;
  onReconnect: () => void;
  onDisconnect: () => void;
  onRemove: () => void;
  reconnecting: boolean;
}

const DeviceCard: React.FC<DeviceCardProps> = ({
  device,
  liveConnected,
  onReconnect,
  onDisconnect,
  onRemove,
  reconnecting,
}) => {
  const isBluetooth = device.metadata?.connectionType === 'bluetooth';
  const isConnected = isBluetooth ? liveConnected : device.status === 'connected';

  const DeviceIcon = getDeviceIcon(device.device_type);
  const ConnIcon = getConnectionIcon(device.metadata);

  return (
    <div className={`border rounded-xl transition-all hover:shadow-md ${
      isConnected
        ? 'border-emerald-200 dark:border-emerald-800/40 bg-emerald-50/50 dark:bg-emerald-900/10'
        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
    }`}>
      <div className="flex items-start gap-4 p-4">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isConnected
            ? 'bg-emerald-100 dark:bg-emerald-900/30'
            : 'bg-gray-100 dark:bg-gray-700'
        }`}>
          <DeviceIcon className={`h-5 w-5 ${isConnected ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400'}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm truncate">
              {device.device_name}
            </span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
              isConnected
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-gray-400'}`} />
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>

          <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500 dark:text-gray-400">
            <span className="inline-flex items-center gap-1">
              <ConnIcon className="h-3 w-3" />
              {device.metadata?.connectionType === 'bluetooth' ? 'Bluetooth' : 'WiFi'}
            </span>
            <span className="capitalize">{device.device_type}</span>
            {device.metadata?.connectionType === 'wifi' && device.metadata.ip && (
              <span className="inline-flex items-center gap-1">
                <Signal className="h-3 w-3" />
                {device.metadata.ip}:{device.metadata.port || 9100}
              </span>
            )}
            {device.last_connected && (
              <span>Last: {formatDate(device.last_connected)}</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          {isBluetooth && !isConnected && (
            <button
              onClick={onReconnect}
              disabled={reconnecting}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-white bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 shadow-sm shadow-violet-500/20 transition-all disabled:opacity-50"
              title="Reconnect"
            >
              {reconnecting ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Plug className="h-3.5 w-3.5" />}
              Reconnect
            </button>
          )}
          {isBluetooth && isConnected && (
            <button
              onClick={onDisconnect}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-all"
              title="Disconnect"
            >
              <Unplug className="h-3.5 w-3.5" />
              Disconnect
            </button>
          )}
          <button
            onClick={onRemove}
            className="p-2 rounded-lg text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            title="Remove device"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ────────── Main Page ────────── */

export const DeviceManager: React.FC = () => {
  const {
    devices,
    loading,
    scanning,
    error,
    bleAvailable,
    loadDevices,
    addDevice,
    removeDevice,
    scanBluetooth,
    reconnectBluetooth,
    disconnectBluetooth,
    isBluetoothConnected,
    clearError,
  } = useDeviceManager();

  const [showWifiModal, setShowWifiModal] = useState(false);
  const [showBtTypePicker, setShowBtTypePicker] = useState(false);
  const [wifiSaving, setWifiSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [reconnectingId, setReconnectingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const handleScanBluetooth = useCallback(
    async (type: 'printer' | 'scanner' | 'other') => {
      setShowBtTypePicker(false);
      await scanBluetooth(type);
    },
    [scanBluetooth],
  );

  const handleAddWifi = useCallback(
    async (name: string, type: 'printer' | 'scanner' | 'other', ip: string, port: number) => {
      setWifiSaving(true);
      await addDevice(name, type, 'wifi', { ip, port });
      setWifiSaving(false);
      setShowWifiModal(false);
    },
    [addDevice],
  );

  const handleReconnect = useCallback(
    async (device: ManagedDevice) => {
      setReconnectingId(device.id);
      await reconnectBluetooth(device);
      setReconnectingId(null);
    },
    [reconnectBluetooth],
  );

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadDevices();
    setRefreshing(false);
  }, [loadDevices]);

  const filteredDevices = searchQuery
    ? devices.filter(
        (d) =>
          d.device_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          d.device_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (d.metadata?.ip || '').includes(searchQuery),
      )
    : devices;

  const bluetoothDevices = filteredDevices.filter((d) => d.metadata?.connectionType === 'bluetooth');
  const wifiDevices = filteredDevices.filter((d) => d.metadata?.connectionType === 'wifi');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
            <Power className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Device Manager</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Connect and manage Bluetooth and WiFi printers, scanners, and peripherals
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2.5 rounded-xl border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 text-gray-600 dark:text-gray-400 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowBtTypePicker(true)}
            disabled={scanning || !bleAvailable}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 shadow-sm shadow-violet-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {scanning ? <Loader className="h-4 w-4 animate-spin" /> : <Bluetooth className="h-4 w-4" />}
            {scanning ? 'Scanning...' : 'Scan Bluetooth'}
          </button>
          <button
            onClick={() => setShowWifiModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-sm shadow-blue-500/20 transition-all"
          >
            <Wifi className="h-4 w-4" />
            Add WiFi Device
          </button>
        </div>
      </div>

      {/* Error / Info Banner */}
      {error && (
        <div className="flex items-center justify-between gap-3 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40">
          <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
            <CircleAlert className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
          <button onClick={clearError} className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-800/30 transition-colors">
            <X className="h-4 w-4 text-red-400" />
          </button>
        </div>
      )}

      {!bleAvailable && (
        <div className="flex items-center gap-2 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 text-sm text-amber-700 dark:text-amber-300">
          <CircleAlert className="h-4 w-4 flex-shrink-0" />
          Web Bluetooth is not available. Use Chrome (or Edge) over HTTPS to scan for Bluetooth devices.
        </div>
      )}

      {/* Search */}
      {devices.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search devices by name, type, or IP..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-8 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all placeholder:text-gray-400"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader className="h-8 w-8 text-violet-500 animate-spin" />
        </div>
      ) : devices.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex flex-col items-center justify-center py-20 px-4">
            <div className="w-16 h-16 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center mb-4">
              <Inbox className="h-8 w-8 text-violet-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">No devices added</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-sm">
              Click "Scan Bluetooth" to discover nearby Bluetooth devices, or "Add WiFi Device" to manually add a network printer or scanner.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Bluetooth Devices */}
          {bluetoothDevices.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-5 border-b border-gray-100 dark:border-gray-700/50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 shadow-lg shadow-violet-500/20 flex items-center justify-center">
                    <Bluetooth className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Bluetooth Devices</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Paired via Web Bluetooth</p>
                  </div>
                  <span className="ml-2 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
                    {bluetoothDevices.length}
                  </span>
                </div>
              </div>
              <div className="p-4 space-y-3">
                {bluetoothDevices.map((d) => (
                  <DeviceCard
                    key={d.id}
                    device={d}
                    liveConnected={isBluetoothConnected(d)}
                    onReconnect={() => handleReconnect(d)}
                    onDisconnect={() => disconnectBluetooth(d)}
                    onRemove={() => removeDevice(d.id)}
                    reconnecting={reconnectingId === d.id}
                  />
                ))}
              </div>
            </div>
          )}

          {/* WiFi Devices */}
          {wifiDevices.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-5 border-b border-gray-100 dark:border-gray-700/50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-500/20 flex items-center justify-center">
                    <Wifi className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">WiFi Devices</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Network printers and scanners</p>
                  </div>
                  <span className="ml-2 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                    {wifiDevices.length}
                  </span>
                </div>
              </div>
              <div className="p-4 space-y-3">
                {wifiDevices.map((d) => (
                  <DeviceCard
                    key={d.id}
                    device={d}
                    liveConnected={false}
                    onReconnect={() => {}}
                    onDisconnect={() => {}}
                    onRemove={() => removeDevice(d.id)}
                    reconnecting={false}
                  />
                ))}
              </div>
            </div>
          )}

          {/* No search results */}
          {filteredDevices.length === 0 && searchQuery && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
              <Search className="h-8 w-8 text-gray-400 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">No devices match "{searchQuery}"</p>
            </div>
          )}
        </div>
      )}

      {/* Stats summary */}
      {devices.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Devices', value: devices.length, icon: Monitor, color: 'text-gray-600 dark:text-gray-400', bg: 'bg-gray-100 dark:bg-gray-700' },
            { label: 'Bluetooth', value: devices.filter((d) => d.metadata?.connectionType === 'bluetooth').length, icon: Bluetooth, color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-100 dark:bg-violet-900/30' },
            { label: 'WiFi', value: devices.filter((d) => d.metadata?.connectionType === 'wifi').length, icon: Wifi, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-900/30' },
            { label: 'Connected', value: devices.filter((d) => d.metadata?.connectionType === 'bluetooth' ? isBluetoothConnected(d) : d.status === 'connected').length, icon: CircleCheck, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
          ].map(({ label, value, icon: Icon, color, bg }) => (
            <div key={label} className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center`}>
                  <Icon className={`h-4 w-4 ${color}`} />
                </div>
                <div>
                  <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{value}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showWifiModal && (
        <AddWifiDeviceModal
          onAdd={handleAddWifi}
          onClose={() => setShowWifiModal(false)}
          saving={wifiSaving}
        />
      )}

      {showBtTypePicker && (
        <BtTypeSelector
          onSelect={handleScanBluetooth}
          onClose={() => setShowBtTypePicker(false)}
        />
      )}
    </div>
  );
};
